import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import Skyline from "./Skyline";

const NAV = [
  { label: "What we do", to: "/solutions" },
  { label: "How it works", to: "/how-it-works" },
  { label: "Results", to: "/portfolio" },
  { label: "Ways to work", to: "/#models" },
  { label: "Our story", to: "/about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Skyline animate={false} stroke="#0a0a0a" className="h-10 w-10" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-black ${
                pathname === n.to ? "text-black" : "text-neutral-500"
              }`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/book-demo"
            className="group inline-flex items-center rounded-full bg-[#0a0a0a] text-white text-sm px-6 py-2.5 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-md"
            data-testid="header-book-demo"
          >
            Book a discovery call
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            className="p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="text-lg font-display" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </Link>
              ))}
              <Link to="/book-demo" className="mt-2 inline-flex justify-center rounded-full bg-[#0a0a0a] text-white px-6 py-3" data-testid="mobile-book-demo">
                Book a discovery call
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
